import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const GDPRAddendumPage: React.FC = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-[#161513] py-12 px-4 sm:px-6">
      {/* Logo Background */}
      <div 
        className="absolute top-0 left-0 right-0 h-[400px] bg-cover bg-center bg-no-repeat opacity-20 z-0 pointer-events-none"
        style={{ backgroundImage: `url('/src/assets/logo.gif')` }}
      />
      
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="flex items-center mb-8">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center text-gray-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>
        </div>
        
        <div className="bg-[#161513] rounded-xl shadow-lg border border-[#0CB4E4]/30 overflow-hidden">
          <div className="p-6 md:p-8 border-b border-[#0CB4E4]/20">
            <h1 className="text-3xl font-bold text-white">GDPR Addendum</h1>
            <p className="text-gray-300 mt-2">Effective Date: November 1, 2025</p>
            <p className="text-gray-300 mt-4">
              This addendum applies to users located in the European Union (EU), United Kingdom (UK), 
              and Switzerland, supplementing our main{' '}
              <Link to="/privacy" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                Privacy Policy
              </Link>
              {' '}and{' '}
              <Link to="/terms" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                Terms of Service
              </Link>
              . It outlines your specific rights under the General Data Protection Regulation (GDPR) 
              and related data protection laws.
            </p>
          </div>
          
          <div className="p-6 md:p-8 space-y-6">
            {/* Section 1 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">1. Legal Basis for Processing</h2>
              <p className="text-gray-300 mb-2">
                We process your personal data under the following legal bases:
              </p>
              <ul className="list-disc pl-5 text-gray-300 space-y-2">
                <li><strong>Contractual necessity:</strong> To provide the Service you've signed up for</li>
                <li><strong>Legitimate interests:</strong> To improve our Service, conduct analytics, and ensure security</li>
                <li><strong>Consent:</strong> For marketing communications and optional data processing (which you can withdraw at any time)</li>
                <li><strong>Legal obligations:</strong> To comply with applicable EU/UK laws and regulations</li>
              </ul>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">2. Your GDPR Rights</h2>
              <p className="text-gray-300 mb-2">
                As an EU/UK/Swiss resident, you have the following rights:
              </p>
              <ul className="list-disc pl-5 text-gray-300 space-y-2">
                <li><strong>Right to Access:</strong> Request a copy of your personal data we hold</li>
                <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data</li>
                <li><strong>Right to Erasure ("Right to be Forgotten"):</strong> Request deletion of your personal data</li>
                <li><strong>Right to Restriction:</strong> Request limitation of processing in certain circumstances</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests or for direct marketing</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent</li>
                <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local data protection authority</li>
              </ul>
              <p className="text-gray-300 mt-3">
                To exercise any of these rights, please contact us at{' '}
                <a href="mailto:privacy@cr3ate.ai" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                  privacy@cr3ate.ai
                </a>
                . We will respond within 30 days of your request.
              </p>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">3. International Data Transfers</h2>
              <p className="text-gray-300">
                As Ydrive Inc. is based in the United States, your personal data may be transferred to 
                and processed in countries outside the EU/UK/Switzerland. We ensure adequate protection 
                through:
              </p>
              <ul className="list-disc pl-5 text-gray-300 space-y-2 mt-2">
                <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
                <li>Data Processing Agreements with third-party processors</li>
                <li>Technical and organizational security measures equivalent to GDPR standards</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">4. Data Retention</h2>
              <p className="text-gray-300">
                We retain your personal data only as long as necessary to:
              </p>
              <ul className="list-disc pl-5 text-gray-300 space-y-2 mt-2">
                <li>Provide the Service to you</li>
                <li>Comply with legal obligations (e.g., tax, accounting requirements)</li>
                <li>Resolve disputes and enforce our agreements</li>
              </ul>
              <p className="text-gray-300 mt-3">
                Inactive accounts may be deleted after 6 months, as outlined in our Terms of Service. 
                You can request earlier deletion by contacting us.
              </p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">5. Data Protection Officer</h2>
              <p className="text-gray-300">
                For GDPR-related inquiries or to exercise your rights, you may contact our data 
                protection team at{' '}
                <a href="mailto:privacy@cr3ate.ai" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                  privacy@cr3ate.ai
                </a>
              </p>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">6. Automated Decision-Making</h2>
              <p className="text-gray-300">
                We may use automated processing for service optimization and fraud prevention. 
                You have the right to request human review of any automated decisions that 
                significantly affect you.
              </p>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">7. Marketing Communications</h2>
              <p className="text-gray-300">
                We will only send you marketing emails if you have explicitly consented. You can 
                unsubscribe at any time by clicking the unsubscribe link in our emails or by 
                contacting us at{' '}
                <a href="mailto:privacy@cr3ate.ai" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                  privacy@cr3ate.ai
                </a>
              </p>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">8. Supervisory Authority</h2>
              <p className="text-gray-300">
                If you believe we have not adequately addressed your concerns, you have the right 
                to lodge a complaint with your local data protection supervisory authority:
              </p>
              <ul className="list-disc pl-5 text-gray-300 space-y-2 mt-2">
                <li><strong>EU:</strong> Contact your national data protection authority 
                  (see <a href="https://edpb.europa.eu/about-edpb/board/members_en" 
                  className="text-[#0CB4E4] hover:text-[#0CB4E4]/80" target="_blank" rel="noopener noreferrer">
                  EDPB directory</a>)</li>
                <li><strong>UK:</strong> Information Commissioner's Office (ICO) at{' '}
                  <a href="https://ico.org.uk" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80" 
                  target="_blank" rel="noopener noreferrer">ico.org.uk</a></li>
                <li><strong>Switzerland:</strong> Federal Data Protection and Information Commissioner (FDPIC)</li>
              </ul>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">9. Updates to This Addendum</h2>
              <p className="text-gray-300">
                We may update this GDPR Addendum to reflect changes in our practices or legal 
                requirements. Material changes will be communicated via email or prominent notice 
                on our Service.
              </p>
            </section>

            {/* Contact Section */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">Contact Information</h2>
              <p className="text-gray-300">
                For any GDPR-related questions or to exercise your rights:
              </p>
              <div className="mt-3 text-gray-300">
                <p><strong>Email:</strong>{' '}
                  <a href="mailto:privacy@cr3ate.ai" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                    privacy@cr3ate.ai
                  </a>
                </p>
                <p className="mt-2"><strong>Company:</strong> Ydrive Inc.</p>
                <p><strong>Address:</strong> Delaware, United States</p>
              </div>
            </section>
          </div>
          
          <div className="p-6 md:p-8 bg-[#161513]/70 border-t border-[#0CB4E4]/20">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap gap-4">
                <Link to="/privacy" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                  View Privacy Policy
                </Link>
                <Link to="/terms" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                  View Terms of Service
                </Link>
              </div>
              
              <button 
                onClick={() => navigate(-1)}
                className="px-5 py-2.5 bg-[#0C5484] text-white hover:bg-[#0CB4E4]/90 transition-colors shadow-md rounded-lg"
              >
                Back
              </button>
            </div>
          </div>
        </div>

        <footer className="text-center text-gray-400 mt-8">
          <p>© Ydrive Inc.</p>
        </footer>
      </div>
    </div>
  );
};

export default GDPRAddendumPage;